import { z } from 'zod/v4'
import { UIControlSchema } from '@features/ui/types'

import { AIServiceSchema } from '../features/workflowRunSteps/services/ai/types'
import { CRMServiceSchema } from '../features/workflowRunSteps/services/crm/types'
import { ConversationsServiceSchema } from '../features/workflowRunSteps/services/conversations/types'
import { ToolsServiceSchema } from '../features/workflowRunSteps/services/tools/types'
import { ComputeServiceSchema } from '../features/workflowRunSteps/services/compute/types'
import { AppServiceSchema } from '../features/workflowRunSteps/services/app/types'
import { ReportsServiceSchema } from '../features/workflowRunSteps/services/reports/types'
import { ThreadsServiceSchema } from '../features/workflowRunSteps/services/threads/types'
import {
  WorkflowNodeBaseSchema,
  WorkflowStepSubflowSchema,
} from '../features/workflowRunSteps/resources/types'

// Common service name enum
export const ServiceNameSchema = z.enum([
  'skedyul/ai',
  'skedyul/crm',
  'skedyul/conversations',
  'skedyul/tools',
  'skedyul/compute',
  'skedyul/app',
  'skedyul/reports',
  'skedyul/threads',
])

export const CreateWorkflowSchema = z.object({
  fileId: z.string(),
  publish: z.boolean().optional(),
  appId: z.string().optional(),
})

export const UpdateWorkflowSchema = z.object({
  fileId: z.string(),
  workflowId: z.string(),
  appId: z.string().optional(),
})

const WorkflowConfigurationSchema = z.object({
  position: z.object({ x: z.number(), y: z.number() }),
  steps: z.record(
    z.string(),
    z.object({
      id: z.string(),
      position: z.object({ x: z.number(), y: z.number() }),
    }),
  ),
})

export type CreateWorkflowType = z.infer<typeof CreateWorkflowSchema>
export type UpdateWorkflowType = z.infer<typeof UpdateWorkflowSchema>

const WorkflowOutputSchema = z.object({
  description: z.string().min(1).max(255),
  type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
  value: z.string().optional(),
})

const WorkflowWebhookSchema = z.object({
  name: z.string().min(1).max(80),
  description: z.string().min(1).max(255),
  expectedHeaders: z.array(z.string()).optional(),
  expectedBody: z.string().optional(),
})

export const WorkflowFileInputSchema = z.object({
  label: z.string().min(1).max(80),
  /**
   * Free-form input type. Scalars include `string`, `number`, `id`, and
   * `liquid` (a string that is resolved then rendered once when a step
   * interpolates `{{ inputs.<name> }}`). Structured types are `object`,
   * `@app/…`, and `@crm/…`.
   */
  type: z.string().min(1).max(80),
  required: z.boolean().optional(),
  description: z.string().min(1).max(255),
  control: UIControlSchema.optional(),
})

const WorkflowStepNodeTaskSchema = z.union([
  // correct types
  AIServiceSchema,
  CRMServiceSchema,
  ConversationsServiceSchema,
  ToolsServiceSchema,
  ComputeServiceSchema,
  AppServiceSchema,
  ReportsServiceSchema,
  ThreadsServiceSchema,
])

const WorkflowStepNodeSubflowSchema = WorkflowStepSubflowSchema

const WorkflowStepNodeSchema = z.union([
  WorkflowStepNodeTaskSchema,
  WorkflowStepNodeSubflowSchema,
])

const WorkflowTriggerSchema = z.object({
  name: z.string().min(1).max(80),
  description: z.string().min(1).max(255),
})

const WorkflowTriggerWebhookSchema = WorkflowTriggerSchema.extend({
  type: z.literal('webhook'),
})

const WorkflowTriggerAgentSchema = WorkflowTriggerSchema.extend({
  type: z.literal('agent'),
})

const WorkflowTriggerActionSchema = WorkflowTriggerSchema.extend({
  type: z.literal('action'),
  options: z.object({
    label: z.string().min(1).max(80),
    icon: z.string().min(1).max(80),
  }),
})

const WorkflowEnvironmentVariableSchema = z.object({
  required: z.boolean().optional().default(false),
  description: z.string().optional(),
  label: z.string().optional(),
  visibility: z.enum(['encrypted', 'visible']).optional().default('encrypted'),
  editable: z.boolean().optional().default(true),
})

export const WorkflowFileSchema = WorkflowNodeBaseSchema.extend({
  /**
   * the variables for the workflow so that they can be used in the workflow
   */
  variables: z.record(z.string(), z.string()).optional().default({}),

  /**
   * Environment variables required by the workflow
   * Keys are variable names, values define the variable configuration
   */
  environment: z
    .record(z.string(), WorkflowEnvironmentVariableSchema)
    .optional()
    .default({}),

  inputs: z.record(z.string(), WorkflowFileInputSchema),
  outputs: z.record(z.string(), WorkflowOutputSchema),
  steps: z.record(z.string(), WorkflowStepNodeSchema),
  handle: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-_\/]+$/),
}).omit({
  type: true,
})

export type WorkflowFileContentInputType = z.infer<typeof WorkflowFileSchema>
export type WorkflowStepNode = z.infer<typeof WorkflowStepNodeSchema>
export type WorkflowStepNodeTask = z.infer<typeof WorkflowStepNodeTaskSchema>
export type WorkflowStepNodeSubflow = z.infer<
  typeof WorkflowStepNodeSubflowSchema
>
export type WorkflowConfiguration = z.infer<typeof WorkflowConfigurationSchema>
// Service type
export const ServiceSchema = z.union([
  AIServiceSchema,
  CRMServiceSchema,
  ConversationsServiceSchema,
  ToolsServiceSchema,
  ComputeServiceSchema,
  AppServiceSchema,
  ReportsServiceSchema,
  ThreadsServiceSchema,
])
export type WorkflowStepService = z.infer<typeof ServiceSchema>

// Re-export service schemas for convenience

// Configuration to apply during App Installation
export const WorkflowInstallConfigSchema = z.object({
  actions: z
    .array(
      z.object({
        label: z.string(),
        icon: z.string().optional(),
        // Allow targeting by explicit modelId or by app entity handle, which will be resolved at install time
        modelId: z.string().optional(),
        entityHandle: z.string().optional(),
        inputs: z.unknown().optional().default({}),
        batch: z.boolean().optional().default(false),
      }),
    )
    .optional()
    .default([]),
  eventListeners: z
    .array(
      z.object({
        type: z.string(),
        condition: z.string().optional().default('true'),
        inputs: z.unknown().optional().default({}),
      }),
    )
    .optional()
    .default([]),
  webhooks: z
    .array(
      z.object({
        provider: z.string(),
        // provider-specific options
        options: z.record(z.string(), z.unknown()).optional().default({}),
      }),
    )
    .optional()
    .default([]),
})

export type WorkflowInstallConfig = z.infer<typeof WorkflowInstallConfigSchema>

// Build Log Schema
export const BuildLogEntrySchema = z.object({
  timestamp: z.string(),
  level: z.string(),
  message: z.string(),
})

export const BuildLogSchema = z.array(BuildLogEntrySchema)

export type BuildLogEntry = z.infer<typeof BuildLogEntrySchema>
export type BuildLog = z.infer<typeof BuildLogSchema>

// Workflow Run Summary (for lightweight realtime + page enrichment)
export const WorkflowRunSummarySchema = z.object({
  runId: z.string(),
  workflowId: z.string(),
  // Instance context may not always be known (e.g., generic list views)
  instanceId: z.string().optional(),
  workflowName: z.string(),
  status: z.enum(['queued', 'running', 'completed', 'failed', 'canceled']),
  // ISO timestamp when the run last changed in a meaningful way
  updatedAt: z.string(),
})

export type WorkflowRunSummary = z.infer<typeof WorkflowRunSummarySchema>

export const DeployWorkflowFromYamlInputSchema = z.object({
  workplaceId: z.string(),
  memberId: z.string().optional(),
  yamlContent: z.string().min(1),
  publish: z.boolean().optional().default(true),
  versionLabel: z.string().optional(),
})

export type DeployWorkflowFromYamlInput = z.infer<
  typeof DeployWorkflowFromYamlInputSchema
>

export const DeployWorkflowFromYamlResultSchema = z.object({
  workflow: z
    .object({
      id: z.string(),
      handle: z.string(),
      name: z.string(),
      description: z.string(),
    })
    .nullable(),
  version: z
    .object({
      id: z.string(),
      version: z.number().nullable(),
      versionLabel: z.string().nullable(),
      contentHash: z.string().nullable(),
    })
    .nullable(),
  errors: z.record(
    z.string(),
    z
      .object({
        code: z.string(),
        message: z.string(),
      })
      .optional(),
  ),
  isNew: z.boolean(),
  reusedVersion: z.boolean(),
  pendingBuild: z.boolean(),
})

export type DeployWorkflowFromYamlResult = z.infer<
  typeof DeployWorkflowFromYamlResultSchema
>

export const ResolveWorkflowVersionInputSchema = z.object({
  workplaceId: z.string(),
  handle: z.string(),
  version: z.number().optional(),
})

export type ResolveWorkflowVersionInput = z.infer<
  typeof ResolveWorkflowVersionInputSchema
>

export const ResolvedWorkflowVersionSchema = z.object({
  workflowId: z.string(),
  workflowVersionId: z.string(),
  version: z.number(),
  versionLabel: z.string().nullable(),
  triggerVersionId: z.string().nullable(),
})

export type ResolvedWorkflowVersion = z.infer<
  typeof ResolvedWorkflowVersionSchema
>

export const DueActiveWorkflowTagCleanupSchema = z.object({
  workflowRunId: z.string(),
  workplaceId: z.string(),
})

export type DueActiveWorkflowTagCleanup = z.infer<
  typeof DueActiveWorkflowTagCleanupSchema
>
